/**
 * PartnerMarketingCenter — iter110
 * Marketing Support request form (replaces the previous PDF asset library).
 * Partners describe what they need help with and the NoFee Partnership Team
 * responds with a tailored plan within two business days.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Megaphone, CheckCircle2, Send, Sparkles, Clock, Mail, Building2,
} from 'lucide-react';
import PartnerAccountLayout from '../../../components/PartnerAccountLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUPPORT_TYPES = [
  { value: 'campaign_strategy',   label: 'Campaign Strategy' },
  { value: 'co_branded_creative', label: 'Co-Branded Creative' },
  { value: 'social_media_kit',    label: 'Social Media Kit' },
  { value: 'email_outreach',      label: 'Email Outreach Templates' },
  { value: 'press_release',       label: 'Press Release' },
  { value: 'webinar_event',       label: 'Webinar / Event Support' },
  { value: 'video_storytelling',  label: 'Video Storytelling' },
  { value: 'custom_print',        label: 'Custom Print Materials' },
  { value: 'other',               label: 'Other' },
];

const EMPTY_FORM = {
  organization_name: '',
  contact_name: '',
  email: '',
  support_type: '',
  preferred_deadline: '',
  description: '',
};

function HighlightCard({ Icon, title, body }) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 flex gap-3">
      <div className="w-9 h-9 rounded-xl bg-gold-500/10 border border-gold-500/20 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-gold-300" />
      </div>
      <div>
        <p className="text-[13px] font-bold text-white leading-tight">{title}</p>
        <p className="text-[12px] text-white/55 mt-1 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    open:        { label: 'In Review',  cls: 'bg-amber-500/15 text-amber-300 border-amber-500/25' },
    in_progress: { label: 'In Progress', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/25' },
    completed:   { label: 'Delivered',  cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25' },
    closed:      { label: 'Closed',     cls: 'bg-white/10 text-white/55 border-white/15' },
  };
  const m = map[status] || map.open;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${m.cls}`}>
      {m.label}
    </span>
  );
}

export default function PartnerMarketingCenter() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('nofee_partner_token') : null;
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { request_id }
  const [errors, setErrors] = useState({});
  const [requests, setRequests] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // Prefill from partner profile + load history
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/partners/me`, auth)
      .then((r) => {
        const p = r.data?.partner || {};
        setForm((f) => ({
          ...f,
          organization_name: f.organization_name || p.organization_name || '',
          contact_name:      f.contact_name      || p.contact_name      || '',
          email:             f.email             || p.email             || '',
        }));
      })
      .catch(() => {});

    axios.get(`${API}/partners/support/marketing`, auth)
      .then((r) => setRequests(r.data?.items || []))
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  }, [token, auth]);

  const setField = (key) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [key]: v }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.organization_name.trim()) er.organization_name = 'Required.';
    if (!form.contact_name.trim())      er.contact_name      = 'Required.';
    if (!form.email.trim() || !/.+@.+\..+/.test(form.email)) er.email = 'Valid email required.';
    if (!form.support_type)             er.support_type      = 'Choose one.';
    if (!form.description.trim() || form.description.trim().length < 10) {
      er.description = 'Please share at least 10 characters so we understand the goal.';
    }
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/partners/support/marketing`, {
        organization_name: form.organization_name.trim(),
        contact_name:      form.contact_name.trim(),
        email:             form.email.trim(),
        support_type:      form.support_type,
        preferred_deadline: form.preferred_deadline.trim() || undefined,
        description:       form.description.trim(),
      }, auth);
      setSubmitted({ request_id: r.data?.request_id });
      toast.success('Marketing request submitted', {
        description: `Reference ${r.data?.request_id}. We&rsquo;ll reply within 2 business days.`,
      });
      // Refresh history
      try {
        const h = await axios.get(`${API}/partners/support/marketing`, auth);
        setRequests(h.data?.items || []);
      } catch { /* ignore */ }
      // Reset only the variable fields
      setForm((f) => ({
        ...f,
        support_type: '',
        preferred_deadline: '',
        description: '',
      }));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.[0]?.msg || 'Could not submit your request. Please try again.');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return iso.slice(0, 10); }
  };

  return (
    <PartnerAccountLayout
      currentKey="marketing"
      title="Marketing Support"
      subtitle="Tell us what you need — campaigns, creative, outreach, events. Our Partnership Team responds within 2 business days with a tailored plan."
    >
      {/* Value props */}
      <div className="grid sm:grid-cols-3 gap-3 mb-8" data-testid="marketing-support-highlights">
        <HighlightCard
          Icon={Sparkles}
          title="Strategic, not generic"
          body="No PDF libraries. Every request is reviewed by a human and matched to your audience."
        />
        <HighlightCard
          Icon={Clock}
          title="2 business-day response"
          body="You&rsquo;ll receive a tailored plan with deliverables and timeline within 48 hours."
        />
        <HighlightCard
          Icon={Mail}
          title="Direct line to the team"
          body="Reply to the confirmation email anytime to add context or assets."
        />
      </div>

      {submitted && (
        <div
          className="mb-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06] p-5 flex items-start gap-3"
          data-testid="marketing-request-success"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-300 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-[14px] font-bold text-emerald-200">Request submitted</p>
            <p className="text-[12.5px] text-emerald-100/75 mt-1">
              Reference{' '}
              <span className="font-mono font-bold text-emerald-200">{submitted.request_id}</span>.
              Check your email — we&rsquo;ve sent a confirmation and our Partnership Team will reply within 2 business days.
            </p>
          </div>
        </div>
      )}

      {/* Form */}
      <form
        onSubmit={onSubmit}
        className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 sm:p-7"
        data-testid="marketing-request-form"
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gold-500/10 border border-gold-500/20 flex items-center justify-center">
            <Megaphone className="w-4 h-4 text-gold-300" />
          </div>
          <div>
            <h2 className="text-[15px] font-bold text-white">New Marketing Support Request</h2>
            <p className="text-[12px] text-white/50 mt-0.5">Fields marked with <span className="text-rose-300">*</span> are required.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {/* Organization */}
          <div className="sm:col-span-2">
            <label className="block text-[12px] font-bold text-white/70 mb-1.5" htmlFor="mkt-org">
              Organization Name <span className="text-rose-300">*</span>
            </label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
              <input
                id="mkt-org"
                data-testid="marketing-input-organization"
                type="text"
                value={form.organization_name}
                onChange={setField('organization_name')}
                placeholder="e.g. Boston Pizza Association"
                className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/10 focus:border-gold-500/50 focus:bg-white/[0.05] outline-none text-[14px] text-white placeholder-white/30 transition-all"
                maxLength={160}
              />
            </div>
            {errors.organization_name && (
              <p className="text-[11px] text-rose-300 mt-1">{errors.organization_name}</p>
            )}
          </div>

          {/* Contact name */}
          <div>
            <label className="block text-[12px] font-bold text-white/70 mb-1.5" htmlFor="mkt-name">
              Contact Name <span className="text-rose-300">*</span>
            </label>
            <input
              id="mkt-name"
              data-testid="marketing-input-contact-name"
              type="text"
              value={form.contact_name}
              onChange={setField('contact_name')}
              placeholder="Your full name"
              className="w-full px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/10 focus:border-gold-500/50 focus:bg-white/[0.05] outline-none text-[14px] text-white placeholder-white/30 transition-all"
              maxLength={120}
            />
            {errors.contact_name && (
              <p className="text-[11px] text-rose-300 mt-1">{errors.contact_name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-[12px] font-bold text-white/70 mb-1.5" htmlFor="mkt-email">
              Email <span className="text-rose-300">*</span>
            </label>
            <input
              id="mkt-email"
              data-testid="marketing-input-email"
              type="email"
              value={form.email}
              onChange={setField('email')}
              placeholder="you@organization.org"
              className="w-full px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/10 focus:border-gold-500/50 focus:bg-white/[0.05] outline-none text-[14px] text-white placeholder-white/30 transition-all"
            />
            {errors.email && (
              <p className="text-[11px] text-rose-300 mt-1">{errors.email}</p>
            )}
          </div>

          {/* Support Type */}
          <div>
            <label className="block text-[12px] font-bold text-white/70 mb-1.5" htmlFor="mkt-type">
              Support Type <span className="text-rose-300">*</span>
            </label>
            <select
              id="mkt-type"
              data-testid="marketing-input-support-type"
              value={form.support_type}
              onChange={setField('support_type')}
              className="w-full px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/10 focus:border-gold-500/50 focus:bg-white/[0.05] outline-none text-[14px] text-white transition-all appearance-none"
            >
              <option value="" disabled className="bg-zinc-900">Choose support type…</option>
              {SUPPORT_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-zinc-900">
                  {opt.label}
                </option>
              ))}
            </select>
            {errors.support_type && (
              <p className="text-[11px] text-rose-300 mt-1">{errors.support_type}</p>
            )}
          </div>

          {/* Preferred Deadline */}
          <div>
            <label className="block text-[12px] font-bold text-white/70 mb-1.5" htmlFor="mkt-deadline">
              Preferred Deadline <span className="text-white/35 font-normal">(optional)</span>
            </label>
            <input
              id="mkt-deadline"
              data-testid="marketing-input-deadline"
              type="date"
              value={form.preferred_deadline}
              onChange={setField('preferred_deadline')}
              className="w-full px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/10 focus:border-gold-500/50 focus:bg-white/[0.05] outline-none text-[14px] text-white placeholder-white/30 transition-all"
            />
            <p className="text-[11px] text-white/40 mt-1">We&rsquo;ll do our best to align — flexible is fine.</p>
          </div>

          {/* Description */}
          <div className="sm:col-span-2">
            <label className="block text-[12px] font-bold text-white/70 mb-1.5" htmlFor="mkt-desc">
              Description <span className="text-rose-300">*</span>
            </label>
            <textarea
              id="mkt-desc"
              data-testid="marketing-input-description"
              value={form.description}
              onChange={setField('description')}
              rows={6}
              placeholder="Tell us what you&rsquo;re trying to achieve — audience, channels, key message, success metrics, anything we should know."
              className="w-full px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/10 focus:border-gold-500/50 focus:bg-white/[0.05] outline-none text-[14px] text-white placeholder-white/30 transition-all resize-y leading-relaxed"
              maxLength={5000}
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-[11px] text-white/40">
                {errors.description ? (
                  <span className="text-rose-300">{errors.description}</span>
                ) : (
                  'Min 10 characters · Max 5000'
                )}
              </p>
              <p className="text-[11px] text-white/35 font-mono">{form.description.length}/5000</p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="submit"
            disabled={submitting}
            data-testid="marketing-submit-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gold-500 hover:bg-gold-400 disabled:opacity-60 disabled:cursor-not-allowed text-black text-[13.5px] font-bold transition-all"
          >
            <Send className="w-4 h-4" />
            {submitting ? 'Submitting…' : 'Submit Request'}
          </button>
        </div>
      </form>

      {/* History */}
      <section className="mt-10" data-testid="marketing-history-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[15px] font-bold text-white">Your previous requests</h2>
          <span className="text-[12px] text-white/45 font-mono">{requests.length} total</span>
        </div>

        {loadingHistory ? (
          <p className="text-[13px] text-white/50">Loading…</p>
        ) : requests.length === 0 ? (
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 text-center">
            <p className="text-[13px] text-white/55">No requests yet. Submit the form above to get started.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {requests.map((r) => (
              <div
                key={r.id || r.request_id}
                data-testid="marketing-history-row"
                className="rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] p-4 transition-all"
              >
                <div className="flex flex-wrap items-center gap-3 justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono text-[12px] text-gold-300 font-bold">{r.request_id}</span>
                    <span className="text-[13.5px] font-bold text-white truncate">{r.support_type_label || r.support_type}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[11.5px] text-white/55">
                    <StatusPill status={r.status} />
                    <span>{formatDate(r.created_at)}</span>
                  </div>
                </div>
                {r.description && (
                  <p className="mt-2 text-[12.5px] text-white/55 leading-relaxed line-clamp-2">
                    {r.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </PartnerAccountLayout>
  );
}
